<template>
  <div class="">
    <section-title content="Búsqueda de Especialista" :has-line="true" />
    <div class="row">
      <div
        v-if="isSearchByHdnActive"
        :class="submitted ? 'col-md-2' : 'col-md-3'"
      >
        <label class="data-title mb-2">
          SSN o Número de Registro de Salud:
        </label>
        <validation-observer ref="ssnForm" tag="form">
          <validation-provider v-slot="{ errors }" rules="required|max:16">
            <input
              v-if="!disableSsnInput"
              v-model="ssnOrHdn"
              class="form-control input-flat"
              :class="{ invalid: errors.length > 0 }"
              :disabled="disableSsnInput"
              maxlength="16"
              placeholder="Escribir SSN o Número de Registro de Salud"
              type="number"
            />

            <custom-input
              v-if="disableSsnInput"
              :disabled="true"
              :value="maskSSN(ssnOrHdn)"
            />
            <custom-error
              v-if="errors.length"
              class="position-absolute"
              :message="
                errors[0]?.includes('required')
                  ? 'El campo es requerido'
                  : 'No debe exceder 16 caracteres'
              "
              variant="no-border mt-1"
            />
          </validation-provider>
        </validation-observer>
      </div>

      <div v-else :class="submitted ? 'col-md-2' : 'col-md-3'">
        <label class="data-title mb-2">Número de Seguro Social:</label>
        <validation-observer :ref="ssnFormName" v-slot="{}" tag="form">
          <validation-provider
            v-slot="{ errors }"
            name="ssnField"
            rules="required|ssn"
          >
            <custom-input
              v-model="model.ssn"
              :custom-error-msg="errorMessage"
              :disabled="disableSsnInput"
              :error="errors[0]"
              mask-type="Personalssn"
              placeholder="XXX-XX-XXXX"
            />
          </validation-provider>
        </validation-observer>
      </div>
      <div class="col-md-2 mt-0-3">
        <action-button
          v-if="!submitted"
          class="px-4 mt-2rem"
          i="fa fa-search"
          text="Buscar Especialista"
          variant="success"
          @click="search"
        />
        <action-button
          v-else
          class="px-4 mt-2rem"
          i="fas fa-exchange-alt"
          :text="labelButton"
          variant="success"
          @click="onChangeSSN"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ActionButton from 'ActionButton';
import alert from '@quisit/mipe-utils/mixins/alert.mixin.js';
import {
  getSpecialistBySsnOrHdn,
  isSsnValid,
} from '@/services/provider/specialist.api.js';
import { getByParameterName } from '@/services/common/sysparameter.api';
import CustomError from 'custom-error';

export default {
  name: 'SpecialistSsnSearch',
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomInput,
    ActionButton,
    CustomError,
  },
  mixins: [alert],
  props: {
    companyId: {
      type: Number,
      default: () => null,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      model: { ssn: '' },
      isSearchByHdnActive: null,
      ssnOrHdn: '',
      isSsnValid: true,
      submitted: false,
    };
  },
  async mounted() {
    await this.loadSearchMethodParam();
  },
  computed: {
    ssnFormName() {
      return 'ssnForm';
    },
    labelButton() {
      return this.isSearchByHdnActive ? 'Actualizar' : 'Cambiar SSN';
    },
    labelModal() {
      return this.isSearchByHdnActive
        ? 'El sistema reiniciará los datos nuevamente, ¿está seguro(a) que desea cambiar el número introducido?'
        : 'El sistema reiniciará los datos nuevamente, ¿está seguro(a) que desea cambiar el número de SSN?';
    },
    errorMessage() {
      const value = this.model.ssn ?? '';
      if (value.length === 0) return 'El número de seguro social es requerido.';

      if (value.length < 9 || !this.isSsnValid)
        return 'El número de seguro social no es válido.';

      return '';
    },
    disableSsnInput() {
      return this.submitted;
    },
  },
  methods: {
    maskSSN(ssn) {
      if (!ssn) return '';

      // Elimina espacios y guiones existentes
      let cleanSSN = ssn.trim().replace(/\D/g, '');

      // Verifica que tenga 9 dígitos
      if (!/^\d{9}$/.test(cleanSSN)) {
        return ssn; // Si no es válido, retorna el SSN original
      }

      // Obtiene los últimos 4 dígitos
      let last4 = cleanSSN.slice(-4);

      // Devuelve el formato enmascarado
      return `***-**-${last4}`;
    },
    async loadSearchMethodParam() {
      const { data } = await getByParameterName(
        'CreateRPSpecialistBySearchHealthDepartmentNumber'
      );
      this.isSearchByHdnActive = data.value?.toLowerCase() === 'true';
    },
    async search() {
      const searchValue = this.isSearchByHdnActive
        ? this.ssnOrHdn
        : this.model.ssn;

      const isValid = await this.$refs[this.ssnFormName].validate();
      if (!isValid) return;
      const { data: result } = await isSsnValid(searchValue);
      this.isSsnValid = result;

      try {
        const { data } = await getSpecialistBySsnOrHdn(
          searchValue,
          this.companyId
        );
        this.submitted = true;
        this.$emit('search', {
          ...data,
          ssnOrHdn: searchValue,
          isSsnValid: this.isSsnValid,
        });
      } catch (error) {
        this.ShowToast('Alerta', error.response.data.message, 'warning');
      }
    },
    async onChangeSSN() {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        this.labelModal,
        'Sí',
        'No'
      );
      if (!answer) return;

      this.model.ssn = '';
      this.ssnOrHdn = '';
      this.$refs[this.ssnFormName].reset();
      this.submitted = false;
      this.$emit('change-ssn');
    },
  },
};
</script>
<style scoped>
.mt-0-3 {
  margin-top: 0.3rem;
}
.ml-n3-5 {
  margin-left: -6rem;
}
.mt-2rem {
  margin-top: 2rem;
}
.input-flat[disabled] {
  background-color: transparent !important;
  border: none !important;
  cursor: default;
  pointer-events: none;
}
</style>
